import { dimensionOf } from "../dimensionOf";
import type {
  BuildFolderSuggestionTable,
  CodeOf,
  IndexByCodeOf,
  ToTableAxis,
} from "./types";

const toTableAxis: ToTableAxis = ({ dimension, plans }) => {
  if (dimension === null) return { key: null, values: [null] };
  const hasUnassignedPlan = plans.some((plan) => plan[dimension.key] === null);
  return {
    key: dimension.key,
    values: hasUnassignedPlan ? [...dimension.values, null] : dimension.values,
  };
};

// The null key is the "Not assigned" row or column.
const indexByCodeOf: IndexByCodeOf = ({ axis }) =>
  new Map(axis.values.map((value, index) => [value?.code ?? null, index]));

const codeOf: CodeOf = ({ axis, plan }) =>
  axis.key === null ? null : plan[axis.key];

// Rows are levels; columns are buildings, or trades when sorting by trade or when there are no buildings.
export const buildFolderSuggestionTable: BuildFolderSuggestionTable = ({
  suggestion,
  structure,
}) => {
  const buildings = dimensionOf({ suggestion, key: "building" });
  const trades = dimensionOf({ suggestion, key: "trade" });
  const rows = toTableAxis({
    dimension: dimensionOf({ suggestion, key: "level" }),
    plans: suggestion.plans,
  });
  const columns = toTableAxis({
    dimension: structure === "trade" ? trades : (buildings ?? trades),
    plans: suggestion.plans,
  });

  const floorPlanIdsByCell = rows.values.map(() =>
    columns.values.map((): number[] => []),
  );

  const planCountByRow = new Array<number>(rows.values.length).fill(0);
  const planCountByColumn = new Array<number>(columns.values.length).fill(0);

  const rowIndexByCode = indexByCodeOf({ axis: rows });
  const columnIndexByCode = indexByCodeOf({ axis: columns });
  for (const plan of suggestion.plans) {
    const rowIndex = rowIndexByCode.get(codeOf({ axis: rows, plan }));
    const columnIndex = columnIndexByCode.get(codeOf({ axis: columns, plan }));
    if (rowIndex === undefined || columnIndex === undefined) continue;
    floorPlanIdsByCell[rowIndex][columnIndex].push(plan.floorPlanId);
    planCountByRow[rowIndex] += 1;
    planCountByColumn[columnIndex] += 1;
  }

  return {
    rows,
    columns,
    floorPlanIdsByCell,
    planCountByRow,
    planCountByColumn,
  };
};
